import {
  PortableText,
  type PortableTextBlock,
  type PortableTextReactComponents,
} from '@portabletext/react'
import clsx from 'clsx'
import type React from 'react'

import { RichTextBlock } from '@/components/Sections/SectionRichText/RichTextBlock'
import { Tag } from '@/components/Tag'
import type { CMSTagTheme } from '@/types/sanity'

import './SimpleSelectableOption.scss'

type SimpleSelectableOptionProps = {
  type: 'radio' | 'checkbox'
  id: string
  name: string
  value: string | number
  label: string
  tag?: string
  tagTheme?: CMSTagTheme
  description?: PortableTextBlock[]
  subcontent?: string
  checked?: boolean
  disabled?: boolean
  loading?: boolean
  styling?: 'normal' | 'borderless'
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onClick?: React.MouseEventHandler<HTMLInputElement>
}

const portableTextComponents: Partial<PortableTextReactComponents> = {
  block: RichTextBlock,
  types: {},
}

export const SimpleSelectableOption: React.FC<SimpleSelectableOptionProps> = ({
  type,
  id,
  name,
  value,
  label,
  tag,
  tagTheme,
  description,
  subcontent,
  checked = false,
  disabled = false,
  loading = false,
  styling = 'normal',
  onChange,
  onClick,
  ...props
}) => {
  const classes = [
    'simple-selectable-option',
    disabled && 'simple-selectable-option--disabled',
    loading && 'simple-selectable-option--loading',
    styling !== 'normal' && `simple-selectable-option--${styling}`,
  ]

  const inputClasses = [
    'simple-selectable-option__input',
    `simple-selectable-option__input--${type}`,
  ]

  return (
    <div className={clsx(classes)}>
      <label
        htmlFor={id}
        className={`simple-selectable-option__label ${
          checked ? 'simple-selectable-option--selected' : ''
        }`}
      >
        <span className="simple-selectable-option__selected-indicator" />
        <div className="simple-selectable-option__body">
          <aside className="simple-selectable-option__input-container">
            <input
              className={clsx(inputClasses)}
              type={type}
              name={name}
              id={id}
              value={value}
              checked={checked}
              disabled={disabled || loading}
              onChange={onChange}
              onClick={onClick}
              {...props}
            />
          </aside>
          <div className="simple-selectable-option__title-container">
            <span>{label}</span>
            {tag && (
              <span className="simple-selectable-option__tag-container">
                <Tag
                  type="compact"
                  text={tag}
                  theme={tagTheme}
                  enforceTheme={true}
                />
              </span>
            )}
          </div>
          <aside className="simple-selectable-option__aside">
            <span className="simple-selectable-option__sub-content">
              {subcontent}
            </span>
          </aside>
        </div>
        {description && (
          <span className="simple-selectable-option__description type-body">
            <PortableText
              value={description}
              components={portableTextComponents}
            />
          </span>
        )}
      </label>
    </div>
  )
}
